import * as React from 'react';
import classNames from 'clsx';
import style from '../styles/TooltipAndTexts.module.scss';
import { ConsentText } from '../ConsentText/ConsentText';
import type { usePortalPopper } from '../../../../providers/usePortalPopper';

type Attribues = ReturnType<typeof usePortalPopper>['attributes'];
type PopperStyles = ReturnType<typeof usePortalPopper>['styles'];

export type TooltipBoxProps = {
  compId: string;
  showPopper: boolean;
  text: string;
  isMobile: boolean;
  popperStyles: PopperStyles;
  attributes: Attribues;
  tooltipButtonRef: React.RefObject<HTMLButtonElement>;
  tooltipOnKeyUp: React.KeyboardEventHandler<HTMLDivElement>;
  setArrowElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  setPopperTargetElem: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  setIsAnimationEnd: (value: boolean) => void;
  setShowPopper: (value: boolean) => void;
  setIsTooltipClicked: (value: boolean) => void;
  showPopperTooltip: () => void;
  hidePopperTooltip: () => void;
  onSettingsClick: () => void;
};

const TooltipBox: React.FC<TooltipBoxProps> = ({
  compId,
  text,
  isMobile,
  showPopper,
  setPopperTargetElem,
  setIsTooltipClicked,
  setShowPopper,
  setArrowElement,
  onSettingsClick,
  popperStyles,
  tooltipButtonRef,
  showPopperTooltip,
  hidePopperTooltip,
  attributes,
  tooltipOnKeyUp,
  setIsAnimationEnd,
}) => {
  const [isTooltipLinkClicked, setIsTooltipLinkClicked] = React.useState(false);

  const onSettingsClickedWithFocus = () => {
    setIsTooltipLinkClicked(true);
    onSettingsClick();
  };

  return (
    <div
      id={`${compId}-tooltip`}
      data-hook={`${compId}-tooltip`}
      ref={setPopperTargetElem}
      style={popperStyles.popper}
      className={classNames(style.tooltip, {
        [style.showTooltip]: showPopper,
      })}
      tabIndex={-1}
      role="tooltip"
      aria-labelledby={`${compId}-tooltip-text`}
      onMouseEnter={showPopperTooltip}
      onMouseLeave={hidePopperTooltip}
      onKeyUp={tooltipOnKeyUp}
      onAnimationEnd={() => {
        setIsAnimationEnd(true);
      }}
      onTransitionEnd={event => {
        if (
          window.getComputedStyle(event.currentTarget).opacity !== '1' &&
          !isMobile
        ) {
          // when keyboard focus-within is removed we move the focus back to the button
          setShowPopper(false);
          setIsTooltipClicked(false);
          isTooltipLinkClicked
            ? setIsTooltipLinkClicked(false)
            : tooltipButtonRef?.current?.focus();
        }
      }}
      {...attributes.popper}
    >
      <div className={style.animationBox}>
        <div id={`${compId}-tooltip-text`} className={style.tooltipTextBox}>
          <ConsentText
            text={text}
            textButtonClass={style.tooltipTextButton}
            onSettingsClick={onSettingsClickedWithFocus}
          />
        </div>
        <div
          ref={setArrowElement}
          className={style.arrow}
          style={popperStyles.arrow}
        />
      </div>
    </div>
  );
};

export default TooltipBox;
